/**
 * My searches API client (2026-08-11).
 *
 * Mirrors lib/api/watches.ts: every call sends a Clerk session Bearer token
 * (from useAuth().getToken()), verified server-side by get_current_user, so a
 * user only ever reads/acts on their OWN searches. Saving searches is a
 * separate, public path in the search flow (anonymous searches are saved too).
 */

import { getOrCreateAnonSession } from "@/lib/anon-session";

function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return (
      process.env.API_INTERNAL_URL ??
      process.env.API_URL ??
      "http://localhost:8000"
    );
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

export interface SearchView {
  id: string;
  label: string;
  country_code: string;
  best_true_final_price_aud: string | null;
  best_retailer_slug: string | null;
  best_product_url: string | null;
  /** Saved /c/<slug> page for "See all prices" (null → fall back to /compare). */
  resolved_slug: string | null;
  /**
   * The search's own walk-away job that has a saved result (matched by query).
   * When set, "See all prices" opens the reliable /s/<job_id> instant view
   * instead of re-running - even when resolved_slug is missing.
   */
  saved_job_id: string | null;
  created_at: string;
}

export class SearchesApiError extends Error {
  status: number;
  // A shopper-facing message the API deliberately sent (e.g. the active-watch
  // cap). Null for every other failure so callers keep their generic copy.
  serverMessage: string | null;
  // Machine-readable code from the same body (e.g. "watch_limit_reached").
  // Callers branch on THIS, never on message text.
  serverCode: string | null;
  constructor(
    status: number,
    message: string,
    serverMessage: string | null = null,
    serverCode: string | null = null,
  ) {
    super(message);
    this.status = status;
    this.serverMessage = serverMessage;
    this.serverCode = serverCode;
  }

  isWatchLimit(): boolean {
    return this.serverCode === "watch_limit_reached";
  }
}

/** Extract FastAPI's {"detail": {"error": "...", "message": "..."}} shape. */
async function _serverDetail(
  res: Response,
): Promise<{ message: string | null; code: string | null }> {
  try {
    const body = (await res.json()) as {
      detail?: { message?: unknown; error?: unknown };
    };
    const msg = body?.detail?.message;
    const code = body?.detail?.error;
    return {
      message: typeof msg === "string" && msg.trim() ? msg : null,
      code: typeof code === "string" && code ? code : null,
    };
  } catch {
    // Non-JSON body - no deliberate message.
    return { message: null, code: null };
  }
}

export async function listSearches(token: string): Promise<SearchView[]> {
  const res = await fetch(`${apiBaseUrl()}/api/searches`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
  });
  if (!res.ok) {
    throw new SearchesApiError(
      res.status,
      `GET /api/searches returned ${res.status}`,
    );
  }
  return (await res.json()) as SearchView[];
}

/** Record that this signed-in user viewed a saved /c comparison page, so
 * Browse / chip / direct-link visits also land in My searches (not just typed
 * searches). Best-effort + fire-and-forget: any failure is swallowed so it can
 * never affect the page. */
export async function recordComparisonView(
  slug: string,
  token: string,
): Promise<void> {
  try {
    // 2026-09-25: the browser's own id travels with the signed-in view, so
    // the api can store it on the row and the anonymous click log can be
    // joined to the account (the log itself never carries the account).
    const sticker = getOrCreateAnonSession();
    await fetch(`${apiBaseUrl()}/api/searches/record-view`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(sticker ? { "x-mio-session": sticker } : {}),
      },
      body: JSON.stringify({ slug }),
      keepalive: true,
    });
  } catch {
    /* best-effort: never surface a view-record failure */
  }
}

/** Convert a saved search into a watch. On success the item leaves My searches
 * and appears in My watches. Returns the new watch id. */
export async function startWatching(
  searchId: string,
  token: string,
): Promise<{ watch_id: string }> {
  const res = await fetch(`${apiBaseUrl()}/api/searches/${searchId}/watch`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    const detail = await _serverDetail(res);
    throw new SearchesApiError(
      res.status,
      `POST /api/searches/${searchId}/watch returned ${res.status}`,
      detail.message,
      detail.code,
    );
  }
  return (await res.json()) as { watch_id: string };
}
